import { action, autorun, computed, makeObservable, observable } from 'mobx'
import { RefObject } from 'react'

import { AvailabilityBlockSource } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { notNullOrUndefinedFilter } from '@avantstay/backoffice-core'

import { PeriodKind } from '@new/__global/components/TapeChart/TapeChart.types'
import { TapeBlockView } from '@new/domains/bookingsTapeChart/BookingsTapeChart.types'
import { BookingsTapeChartStore } from '@new/domains/bookingsTapeChart/stores/BookingsTapeChartStore'
import { TapeChartBlockStore } from '@new/domains/bookingsTapeChart/stores/TapeChartBlockStore'
import { TapeType } from '@new/domains/fastBookingsTapeChart/__types'
import { scrollController } from '@new/domains/fastBookingsTapeChart/ScrollController'
import { fastBookingsTapeChartFiltersStore } from '@new/domains/fastBookingsTapeChart/stores/FastBookingsTapeChartFiltersStore'
import {
  fastBookingsTapeChartStore,
  ParentBlock,
} from '@new/domains/fastBookingsTapeChart/stores/FastBookingsTapeChartStore'

import { SidePanelStore, SidePanelType } from '@stores/SidePanelStore'

export interface BlockAPI {
  setSelected: (selected: boolean) => void
  domRef: RefObject<HTMLDivElement>
}

const getParentBlock = ({ bookingId, id }: ParentBlock) => {
  if (bookingId) {
    const booking = fastBookingsTapeChartStore.getBookingById(bookingId)
    return (
      booking && {
        ...booking,
        period: {
          start: booking.checkInTiming.effectiveTime,
          end: booking.checkOutTiming.effectiveTime,
        },
        note: booking.notes,
        source: AvailabilityBlockSource.booking,
      }
    )
  }
  if (id) {
    return fastBookingsTapeChartStore.getBlockById(id)
  }
  return undefined
}

interface Tape {
  ids: string
  type: TapeType
}

export class TapesStore {
  constructor() {
    makeObservable(this, {
      selectionSourceTape: observable,
      tapeRefs: observable,
      selectedFamily: computed,
      selectedBlocks: computed,
      selectParent: action.bound,
      saveTapeApi: action.bound,
      disposeTapeRef: action.bound,
      selectFamily: action.bound,
      select: action.bound,
      deselect: action.bound,
      showTapeSidePanel: action.bound,
    })

    autorun(() => {
      if (this.selectedBlocks.length > 0) {
        const firstSelectedBlock = tapesStore.selectedBlocks[0]
        const selectedBlock = tapesStore.tapeRefs[firstSelectedBlock.id]
        if (selectedBlock && selectedBlock.domRef.current) {
          scrollController.scrollIntoView(selectedBlock.domRef.current)
          selectedBlock.setSelected(true)
        }
      }
    })
  }
  selectionSourceTape: Tape | undefined = undefined

  tapeRefs: Record<string, BlockAPI> = {}

  get selectedFamily() {
    return this.selectionSourceTape ? this.getTapeFamily(this.selectionSourceTape.ids) : []
  }

  get selectedBlocks(): TapeBlockView[] {
    if (!this.selectionSourceTape) {
      return []
    }
    try {
      return this.selectionSourceTape.ids
        .split(',')
        .map(
          this.selectionSourceTape.type === TapeType.BlockedPeriod
            ? this.getBlockData
            : this.getBookingData,
        )
        .filter(notNullOrUndefinedFilter)
    } catch (e) {
      return []
    }
  }

  getTapeFamily(tapeIds: string) {
    const familySet = new Set([tapeIds])
    return Array.from(
      fastBookingsTapeChartStore.relatedBlocks
        .filter(({ id, relatedIds }) => tapeIds === id || relatedIds.includes(tapeIds))
        .reduce<Set<string>>((family, current) => {
          family.add(current.id)
          current.relatedIds.forEach(id => id && family.add(id))
          return family
        }, familySet),
    )
  }

  getBookingData = (blockId: string): TapeBlockView | undefined => {
    const block = fastBookingsTapeChartStore.getBookingById?.(blockId)
    if (!block) {
      return undefined
    }

    return {
      ...block,
      startDate: block.checkInTiming.effectiveTime,
      endDate: block.checkOutTiming.effectiveTime,
      kind: PeriodKind.block,
      reason: block.source, // legacy TC logic
      note: block.notes,
    }
  }

  getBlockData(blockId: string): TapeBlockView | undefined {
    const block = fastBookingsTapeChartStore.getBlockById(blockId)
    if (!block) {
      return undefined
    }

    const parent = block.parent ? getParentBlock(block.parent) : undefined

    return {
      ...block,
      startDate: block.period.start,
      endDate: block.period.end,
      kind: PeriodKind.block,
      reason: block.source, // legacy TC logic
      parent,
      customOpenParentAction: parent ? () => tapesStore.selectParent(parent) : undefined,
    }
  }

  selectParent(parent: ReturnType<typeof getParentBlock>) {
    if (!parent) {
      return
    }
    this.select({
      ids: parent.id,
      type:
        parent.source === AvailabilityBlockSource.booking
          ? TapeType.Booking
          : TapeType.BlockedPeriod,
    })

    const parentBlock = this.tapeRefs[parent.id]?.domRef.current
    if (parentBlock) {
      // parent block ref could be undefined when it was never in viewport
      // as we render only in-view blocks
      scrollController.scrollIntoView(this.tapeRefs[parent.id]?.domRef.current)
    } else {
      // checking if parent block's home is on the current page
      const parenHomeIndex = fastBookingsTapeChartStore.getHomeIndex(parent.homeId)
      if (parenHomeIndex > -1) {
        scrollController.scrollToRow(parenHomeIndex)
      } else {
        fastBookingsTapeChartStore.goToPropertyPage(parent.homeId)
      }
    }
  }

  saveTapeApi(tapeIds: string, ref: BlockAPI) {
    this.tapeRefs[tapeIds] = ref

    // checking if tape should be selected (for tapes instantioned on scroll)
    return this.selectionSourceTape
      ? this.getTapeFamily(tapeIds).includes(this.selectionSourceTape.ids)
      : false
  }

  disposeTapeRef(tapeId: string) {
    delete this.tapeRefs[tapeId]
  }

  selectFamily(family: string[], select: boolean) {
    family.forEach(tapeId => {
      const setSelected = this.tapeRefs[tapeId]?.setSelected
      if (setSelected) {
        // wrapping with setTimeout to avoid rendering clash:
        // Cannot update a component while rendering a different component
        setTimeout(() => setSelected(select), 0)
      }
    })
  }

  select(tape: Tape) {
    if (this.selectionSourceTape?.ids === tape.ids) {
      return
    }

    if (this.selectionSourceTape) {
      this.deselect(!!tape)
    }

    this.selectionSourceTape = tape
    this.selectFamily(this.getTapeFamily(tape.ids), true)

    this.showTapeSidePanel()
  }

  deselect(isSelectingNewTape?: boolean) {
    if (!this.selectionSourceTape) {
      return
    }
    const tapeId = this.selectionSourceTape.ids
    const bookingId = fastBookingsTapeChartFiltersStore.bookingId
    const blockId = fastBookingsTapeChartFiltersStore.blockId

    if (tapeId === bookingId) {
      fastBookingsTapeChartFiltersStore.setBookingId?.(undefined)
    }

    if (tapeId === blockId) {
      fastBookingsTapeChartFiltersStore.setBlockId?.(undefined)
    }

    this.selectFamily(this.getTapeFamily(this.selectionSourceTape.ids), false)
    this.selectionSourceTape = undefined
    if (!isSelectingNewTape) {
      SidePanelStore.setCurrentSidePanel(undefined)
    }
  }

  showTapeSidePanel() {
    if (!this.selectionSourceTape) {
      return
    }

    if (this.selectionSourceTape.type === TapeType.BlockedPeriod) {
      // check if tape is aggregating block
      if (this.selectedBlocks.length > 1) {
        SidePanelStore.setCurrentSidePanel(SidePanelType.tapeChartViewMultipleBlocks, {
          screenHasSecondaryMenu: true,
        })
        return
      }

      if (this.selectedBlocks.length === 1) {
        // show block side panel
        BookingsTapeChartStore.setBlockIdParam(this.selectionSourceTape.ids)
        SidePanelStore.setCurrentSidePanel(SidePanelType.tapeChartViewBlock, {
          screenHasSecondaryMenu: true,
        })
        TapeChartBlockStore.setSelectedBlock(this.selectedBlocks[0])
        return
      }
    }

    // check if tape is reservation
    const booking = fastBookingsTapeChartStore.getBookingById(this.selectionSourceTape.ids)
    if (booking) {
      // show reservation side panel
      BookingsTapeChartStore.setBookingIdParam(booking.id)
      SidePanelStore.setCurrentSidePanel(SidePanelType.tapeChartReservationInfo, {
        screenHasSecondaryMenu: true,
      })
    }
  }
}

export const tapesStore = new TapesStore()
